import React, { useState, useEffect } from "react";
import { Button, Typography, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  reload,
  loadPage,
  addAsset,
  deleteAsset,
  createRecord,
  deleteRecord,
  reset,
} from "../workDiariesSlice";
import { useIntl } from "react-intl";
import ActionsTable from "../../../components/templates/ActionsTable";
import Surface from "../../../components/layouts/Surface";
import UploadModal from "./UploadModal";
import Employee from "../../../components/tables/Employee";
import Date from "../../../components/tables/Date";
import {
  DownloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { generateBranchDownloadURL } from "../../../api/server";
const { Text, Title } = Typography;

const UploadTable = (props) => {
  const { uploadModalVisible, hideUploadModal } = props;
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const intl = useIntl();
  const [deletingDiaryId, setDeletingDiaryId] = useState(null);

  const {
    page: workDiariesPage,
    activeYear,
    activeMonth,
  } = useSelector((state) => state.workDiaries);
  const { current, pageSize, total, items, filter } = workDiariesPage;

  const loading = useSelector(
    (state) => state.pendingActions["workDiaries/loadPage"]
  );
  const deleteLoading = useSelector(
    (state) => state.pendingActions["workDiaries/delete"]
  );

  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );

  useEffect(() => {
    if (activePatient.id) {
      dispatch(loadPage());
    }
  }, [dispatch, activeYear, activeMonth, activePatient.id]);

  const handleUpload = async (
    date,
    selectedFile,
    selectedFileName,
    selectedOrderId,
    selectedEmployeeId
  ) => {
    const extension =
      selectedFile.name.substring(
        selectedFile.name.lastIndexOf(".") + 1,
        selectedFile.name.length
      ) || null;
    const assetName = extension
      ? selectedFileName + "." + extension
      : selectedFileName;
    const params = {
      branchId: activePatient.branch,
      assetName: assetName,
      file: Object.assign(selectedFile, {
        fileName: assetName,
      }),
    };
    const action = await dispatch(addAsset(params));
    if (!action.error) {
      const diaryParams = {
        branchId: activePatient.branch,
        patientId: activePatient.id,
        workDiary: {
          year: +date.format("YYYY"),
          month: +date.format("M"),
          order_id: selectedOrderId,
          employee_id: selectedEmployeeId,
          file_id: action.payload.data.id,
        },
      };
      const createAction = await dispatch(createRecord(diaryParams));
      if (!createAction.error) {
        hideUploadModal();
        dispatch(loadPage());
      }
    }
  };

  const onDelete = async (record) => {
    const title = intl.formatMessage({
      id: "app.work_diary.deleteConfirmTitle",
    });
    const content =
      intl.formatMessage({
        id: "app.work_diary.deleteConfirmContent",
      }) +
      " " +
      record.file.name +
      "?";
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return deletRecord(record);
      },
      onCancel() {},
    });

    const deletRecord = async (record) => {
      setDeletingDiaryId(record.id);
      const assetParams = {
        branchId: activePatient.branch,
        assetId: record.file.id,
      };
      const action = await dispatch(deleteAsset(assetParams));
      if (!action.error) {
        const params = {
          branchId: activePatient.branch,
          patientId: activePatient.id,
          workDiaryId: record.id,
        };
        const deleteAction = await dispatch(deleteRecord(params));
        if (!deleteAction.error) {
          dispatch(loadPage());
        }
      }
    };
  };

  const onDownload = async (record) => {
    const fileId = record.file.id;
    const fileName = record.file.name;
    const {
      data: { url },
    } = await generateBranchDownloadURL(activePatient.branch, fileId);
    openURL(url, fileName);
  };

  const openURL = (url, name) => {
    var downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = "test";
    downloadLink.title = "Download";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <Surface>
      {contextHolder}
      <Title level={4}>
        {intl.formatMessage({
          id: "app.work_diary.uploadedDiariesTitle",
        })}
      </Title>
      <ActionsTable
        borderless={true}
        tableItems={[
          {
            title: "employee",
            render({ record }) {
              return record && record.employee ? (
                <Employee record={record.employee} />
              ) : null;
            },
            width: "200px",
          },
          {
            title: "client_name",
            render({ record }) {
              return record && record.order ? (
                <Text>{record.order.client.name}</Text>
              ) : null;
            },
            width: "130px",
          },
          {
            title: "upload_date",
            width: "110px",
            dataIndex: "start",
            render({ record }) {
              return <Date text={record.created_at} format={"DD/MM/YYYY"} />;
            },
          },
          {
            title: "month",
            render({ record }) {
              return record && record.month ? (
                <Text>{record.month}</Text>
              ) : null;
            },
            width: "60px",
          },
          {
            title: "year",
            render({ record }) {
              return record && record.year ? <Text>{record.year}</Text> : null;
            },
            width: "60px",
          },
          {
            title: "file_name",
            render({ record }) {
              return record && record.file ? (
                <Text>{record.file.name}</Text>
              ) : null;
            },
            width: "100px",
          },
          {
            title: "actions",
            width: "80px",
            render({ record }) {
              return (
                <div>
                  <Button
                    type="text"
                    onClick={() => {
                      onDownload(record);
                    }}
                  >
                    <DownloadOutlined style={{}} />
                  </Button>
                  <Button
                    type="text"
                    loading={deleteLoading && deletingDiaryId === record.id}
                    onClick={() => {
                      onDelete(record);
                    }}
                  >
                    <DeleteOutlined style={{ color: "red" }} />
                  </Button>
                </div>
              );
            },
          },
        ]}
        deep={true}
        ltrItems={[]}
        category={"work_diary"}
        current={current}
        pageSize={pageSize}
        total={total}
        loading={loading}
        textFilter={filter.freeText}
        reload={reload}
        loadPage={loadPage}
        resetPage={reset}
        items={items}
        showheader={false}
      />
      <UploadModal
        visible={uploadModalVisible}
        handleCancel={hideUploadModal}
        handleUpload={handleUpload}
      />
    </Surface>
  );
};

export default UploadTable;
